import { motion } from "framer-motion";
import { ShieldCheck, ArrowUpRight } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

const points = [
  ["Quant strategies", "Institutionally proven systematic strategies, available to verified Credit Stone clients."],
  ["Discreet allocations", "Mandate sizes from $250k. Onboarding by introduction; no public marketing materials."],
  ["Audited performance", "Independently audited monthly. NAV reconciled to Credit Stone Digital in real time."],
];

export default function Partner() {
  return (
    <section
      id="partner"
      className="relative bg-[#070809] py-28 sm:py-36 lg:py-44 border-y border-[#1F2126] overflow-hidden"
      data-testid="partner-section"
    >
      {/* faint texture */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.06]"
        style={{
          background:
            "radial-gradient(60% 50% at 50% 30%, rgba(255,255,255,0.5) 0%, rgba(0,0,0,0) 60%)",
        }}
      />

      <div className="relative max-w-[1700px] mx-auto px-5 sm:px-10 lg:px-12">
        <div className="grid grid-cols-12 gap-6 mb-14 sm:mb-20">
          <div className="col-span-12 md:col-span-3 t-mono text-[#7A7A78]">
            — § 06 / Exclusive Partner
          </div>
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10% 0px" }}
            transition={{ duration: 1.2, ease }}
            className="col-span-12 md:col-span-9 t-display text-[#F4F2EC] text-3xl sm:text-4xl lg:text-5xl leading-[1] tracking-[-0.035em] max-w-3xl"
          >
            Yield, generated by{" "}
            <span className="text-[#7A7A78]">invitation only.</span>
          </motion.h3>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10% 0px" }}
          transition={{ duration: 1.2, ease, delay: 0.1 }}
          className="rounded-3xl border border-[#1F2126] bg-gradient-to-b from-[#0E1014] to-[#0A0B0D] p-6 sm:p-10 lg:p-14 grid grid-cols-12 gap-8 lg:gap-12"
        >
          {/* Left: brand block */}
          <div className="col-span-12 lg:col-span-5 flex flex-col gap-8">
            <div className="inline-flex items-center gap-2 self-start rounded-full border border-[#2A2C30] px-3 py-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-[#F4F2EC]" strokeWidth={1.5} />
              <span className="t-mono text-[#F4F2EC]">CSD × SuisseQuant</span>
            </div>

            <div>
              <h4
                className="t-display text-[#F4F2EC] select-none"
                data-testid="suissequant-heading"
                style={{
                  fontSize: "clamp(2rem, 6vw, 3.25rem)",
                  letterSpacing: "-0.035em",
                  lineHeight: 1,
                  fontWeight: 300,
                }}
              >
                SuisseQuant
              </h4>
              <div className="mt-3 t-mono text-[#7A7A78]">
                Exclusive yield-generating partner · Private clients only
              </div>
            </div>

            <p className="t-body text-sm sm:text-base text-[#BFBDB6] leading-relaxed max-w-md">
              SuisseQuant is Credit Stone Digital's exclusive yield partner —
              a systematic capital firm, available to verified
              CSD private clients. Mandates are allocated by introduction and
              monitored from your portfolio in real time.
            </p>

            <div className="flex items-center gap-3 mt-2">
              <a
                href="https://www.suissequant.io"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 t-mono px-5 py-3 rounded-full bg-[#F4F2EC] text-[#0A0B0D] hover:bg-white transition-colors"
                data-testid="partner-cta-allocate"
              >
                Request an allocation
                <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
              <button
                className="t-mono text-[#7A7A78] hover:text-[#F4F2EC] transition-colors"
                data-testid="partner-cta-deck"
              >
                ↳ Mandate deck (NDA)
              </button>
            </div>
          </div>

          {/* Right: points */}
          <div className="col-span-12 lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-2 sm:divide-x divide-[#1F2126]">
            {points.map((p, i) => (
              <motion.div
                key={p[0]}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10% 0px" }}
                transition={{ duration: 0.9, ease, delay: i * 0.08 }}
                className={`p-4 sm:px-6 sm:py-2 ${i > 0 ? "border-t sm:border-t-0 border-[#1F2126]" : ""}`}
                data-testid={`partner-point-${i}`}
              >
                <div className="t-mono text-[#7A7A78] mb-3">0{i + 1} / 03</div>
                <h4 className="t-body text-[#F4F2EC] text-base sm:text-lg tracking-[-0.015em] font-light">
                  {p[0]}
                </h4>
                <p className="mt-3 t-body text-sm text-[#BFBDB6] leading-relaxed">
                  {p[1]}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* footnote */}
        <div className="mt-8 t-mono text-[#7A7A78] max-w-3xl leading-relaxed">
          SuisseQuant strategies are offered to Credit Stone Digital clients on
          a private-placement basis. Past performance is not a reliable
          indicator of future returns.
        </div>
      </div>
    </section>
  );
}
